<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div :class="['w-full transition-[max-width] duration-300', step === 4 ? 'max-w-2xl' : 'max-w-md']">
      <div class="bg-white rounded-xl shadow-lg p-8">
        <div class="flex justify-center mb-8">
          <img src="/logo-icon.webp" alt="Optyfind" class="h-20 w-auto" />
        </div>

        <!-- Step 1: Identity + Email -->
        <template v-if="step === 1">
          <h2 class="text-2xl font-bold text-gray-900 text-center mb-6">Create your account</h2>
          <form @submit.prevent="handleInitiate" class="space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label for="register-first-name" class="block text-sm font-medium text-gray-700 mb-1">First name</label>
                <input id="register-first-name" v-model="profile.first_name" type="text" autocomplete="given-name" maxlength="100" required placeholder="Jane"
                  class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
              <div>
                <label for="register-last-name" class="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                <input id="register-last-name" v-model="profile.last_name" type="text" autocomplete="family-name" maxlength="100" required placeholder="Smith"
                  class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email address</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <input v-model="email" type="email" autocomplete="email" required placeholder="you@example.com"
                  class="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
            </div>
            <div v-if="error" class="rounded-lg bg-red-50 p-3 text-red-600 text-sm text-center">{{ error }}</div>
            <button type="submit" :disabled="loading"
              class="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 transition-colors">
              {{ loading ? 'Sending code...' : 'Continue' }}
            </button>
            <p class="text-center text-xs text-gray-400">Free forever. No credit card required.</p>
          </form>
        </template>

        <!-- Step 2: Code entry -->
        <template v-else-if="step === 2">
          <h2 class="text-2xl font-bold text-gray-900 text-center mb-2">Check your email</h2>
          <p class="text-sm text-gray-500 text-center mb-6">
            We sent a 6-digit code to <strong>{{ email }}</strong>. Enter it below.
          </p>
          <form @submit.prevent="handleVerify" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
              <input v-model="code" type="text" inputmode="numeric" maxlength="6" required placeholder="123456"
                class="block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-center text-2xl font-mono tracking-widest placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
            </div>
            <div v-if="error" class="rounded-lg bg-red-50 p-3 text-red-600 text-sm text-center">{{ error }}</div>
            <button type="submit" :disabled="loading"
              class="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 transition-colors">
              {{ loading ? 'Verifying...' : 'Verify email' }}
            </button>
            <p class="text-center text-xs text-gray-400">
              Didn't get the email?
              <button type="button" @click="handleInitiate" class="text-brand-600 hover:underline">Resend code</button>
            </p>
          </form>
        </template>

        <!-- Step 3: Success -->
        <template v-else-if="step === 3">
          <div class="text-center py-4">
            <div class="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Email confirmed!</h2>
            <p class="text-sm text-gray-500">Setting up your account…</p>
          </div>
        </template>

        <!-- Step 4: Application profile -->
        <template v-else-if="step === 4">
          <div class="border-t-4 border-brand-600 pt-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wider text-brand-700 mb-2">Optional setup</p>
                <h2 class="text-2xl font-bold text-gray-900">Set up application autofill</h2>
              </div>
              <span class="shrink-0 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600">Step 3 of 4</span>
            </div>
            <p class="mt-3 text-sm leading-6 text-gray-600">
              These details are stored in your Optyfind account and used by the browser extension to fill supported job applications in your browser. Review every application before submitting; Optyfind never submits one for you.
            </p>
            <p class="mt-2 text-xs text-gray-500">You can change or remove this information anytime in Settings.</p>
          </div>

          <form @submit.prevent="handleProfileContinue" class="mt-6 space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label for="register-phone" class="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
                <input id="register-phone" v-model="profile.phone" type="tel" autocomplete="tel" maxlength="40" placeholder="+1 416 555 0199"
                  class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
              <div>
                <label for="register-linkedin" class="block text-sm font-medium text-gray-700 mb-1">LinkedIn profile</label>
                <input id="register-linkedin" v-model="profile.linkedin_url" type="url" autocomplete="url" maxlength="500" placeholder="https://linkedin.com/in/your-name"
                  class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
            </div>

            <div>
              <label for="register-address-1" class="block text-sm font-medium text-gray-700 mb-1">Street address</label>
              <input id="register-address-1" v-model="profile.address_line_1" type="text" autocomplete="address-line1" maxlength="255"
                class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
            </div>

            <div>
              <label for="register-address-2" class="block text-sm font-medium text-gray-700 mb-1">Apartment, suite, or unit</label>
              <input id="register-address-2" v-model="profile.address_line_2" type="text" autocomplete="address-line2" maxlength="255"
                class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label for="register-city" class="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input id="register-city" v-model="profile.city" type="text" autocomplete="address-level2" maxlength="100"
                  class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
              <div>
                <label for="register-postal-code" class="block text-sm font-medium text-gray-700 mb-1">Postal or ZIP code</label>
                <input id="register-postal-code" v-model="profile.postal_code" type="text" autocomplete="postal-code" maxlength="30"
                  class="block w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label for="register-country" class="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select id="register-country" v-model="profile.country_code" @change="onCountryChange"
                  class="block w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm">
                  <option value="">Select country</option>
                  <option v-for="country in countries" :key="country.code" :value="country.code">{{ country.name }}</option>
                </select>
              </div>
              <div>
                <label for="register-state" class="block text-sm font-medium text-gray-700 mb-1">Province or state</label>
                <select id="register-state" v-model="profile.state_province_code" :disabled="states.length === 0"
                  class="block w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100 disabled:text-gray-400 sm:text-sm">
                  <option value="">{{ states.length ? 'Select province or state' : 'Select a country first' }}</option>
                  <option v-for="state in states" :key="state.code" :value="state.code">{{ state.name }}</option>
                </select>
              </div>
            </div>

            <div v-if="error" class="rounded-lg bg-red-50 p-3 text-red-600 text-sm text-center">{{ error }}</div>
            <div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <button type="button" @click="skipProfile" class="text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-4">
                Skip for now
              </button>
              <button type="submit"
                class="flex justify-center py-2.5 px-6 border border-transparent text-sm font-semibold rounded-lg text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors">
                Continue
              </button>
            </div>
          </form>
        </template>

        <!-- Step 5: Password -->
        <template v-else-if="step === 5">
          <div class="flex items-start justify-between gap-4 mb-6">
            <h2 class="text-2xl font-bold text-gray-900">Create a password</h2>
            <span class="shrink-0 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600">Step 4 of 4</span>
          </div>
          <form @submit.prevent="handleComplete" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input v-model="password" type="password" autocomplete="new-password" required placeholder="At least 8 characters"
                  class="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 sm:text-sm" />
              </div>
            </div>
            <div v-if="error" class="rounded-lg bg-red-50 p-3 text-red-600 text-sm text-center">{{ error }}</div>
            <button type="submit" :disabled="loading"
              class="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 transition-colors">
              {{ loading ? 'Creating account...' : 'Create account' }}
            </button>
          </form>
        </template>

        <!-- Login link -->
        <div class="mt-6 text-center border-t border-gray-100 pt-4">
          <router-link to="/login" class="text-sm font-medium text-brand-600 hover:text-brand-500">
            Already have an account? Sign in
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { fetchCountries, fetchStates } from '../services/api';
import type { ApplicationProfile, Country, StateProvince } from '../types';

export default {
  name: 'UserRegister',
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const step = ref<number>(1);
    const email = ref<string>('');
    const code = ref<string>('');
    const password = ref<string>('');
    const error = ref<string>('');
    const loading = ref<boolean>(false);
    const countries = ref<Country[]>([]);
    const states = ref<StateProvince[]>([]);
    const profile = ref<ApplicationProfile>({
      first_name: '',
      last_name: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      postal_code: '',
      linkedin_url: '',
      country_code: '',
      state_province_code: '',
    });

    const loadCountries = async (): Promise<void> => {
      if (countries.value.length) return;
      try {
        const response = await fetchCountries();
        countries.value = response.data.countries;
      } catch {
        error.value = 'Countries could not be loaded. You can skip this step and add your details later in Settings.';
      }
    };

    const onCountryChange = async (): Promise<void> => {
      profile.value.state_province_code = '';
      states.value = [];
      if (!profile.value.country_code) return;
      try {
        const response = await fetchStates(profile.value.country_code);
        states.value = response.data.states;
      } catch {
        // State or province is optional; registration can continue.
      }
    };

    const handleInitiate = async (): Promise<void> => {
      error.value = '';
      loading.value = true;
      try {
        await authStore.registerInitiate(
          profile.value.first_name,
          profile.value.last_name,
          email.value
        );
        step.value = 2;
      } catch (err: any) {
        error.value = err.response?.data?.error || 'Failed to send code. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const handleVerify = async (): Promise<void> => {
      error.value = '';
      loading.value = true;
      try {
        await authStore.registerVerify(email.value, code.value);
        step.value = 3;
        setTimeout(() => {
          error.value = '';
          step.value = 4;
          loadCountries();
        }, 1500);
      } catch (err: any) {
        error.value = err.response?.data?.error || 'Invalid or expired code.';
      } finally {
        loading.value = false;
      }
    };

    const handleProfileContinue = (): void => {
      error.value = '';
      step.value = 5;
    };

    const skipProfile = (): void => {
      const { first_name, last_name } = profile.value;
      profile.value = {
        first_name,
        last_name,
        phone: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        linkedin_url: '',
        country_code: '',
        state_province_code: '',
      };
      states.value = [];
      error.value = '';
      step.value = 5;
    };

    const handleComplete = async (): Promise<void> => {
      error.value = '';
      if (password.value.length < 8) {
        error.value = 'Password must be at least 8 characters long';
        return;
      }
      loading.value = true;
      try {
        await authStore.registerComplete(email.value, password.value, profile.value);
        router.push('/profile-setup');
      } catch (err: any) {
        error.value = err.response?.data?.error || 'Registration failed. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    return {
      step,
      email,
      code,
      password,
      error,
      loading,
      profile,
      countries,
      states,
      onCountryChange,
      handleInitiate,
      handleVerify,
      handleProfileContinue,
      skipProfile,
      handleComplete,
    };
  },
};
</script>
